/**
 * Booking Rules on a session type (frontend mirror of the backend columns on
 * `schedule_session_type`): OD-1097 (multiple bookings per participant),
 * OD-1102 (no-show / cancellation limit), OD-1111 (concurrent moderator
 * bookings) and the group booking size cap. Each rule is independent and
 * off/unset by default, matching legacy (pre-feature) behaviour.
 */

export interface BookingRules {
  allowMultipleBookingsPerParticipant: boolean; // OD-1097
  maxSessionsPerParticipant?: number; // OD-1097 (+ OD-1171), required when the toggle above is true (>= 2)
  noShowCancellationLimit?: number; // OD-1102, optional, no toggle (>= 1)
  allowConcurrentModeratorBookings: boolean; // OD-1111
  maxConcurrentModeratorSessions?: number; // OD-1111, required when the toggle above is true (>= 2)
  /** When false, group booking size is UNLIMITED for this session type. */
  capGroupBookingSize: boolean;
  /** Required when capGroupBookingSize is true (>= 2); undefined means no cap. */
  maxGroupSize?: number;
  capParticipantSessionsInWindow: boolean;
  windowLimitBasis?: 'hours' | 'sessions';
  windowDays?: number;
  windowCapacity?: number;
  maxHoursPerDay?: number;
}

export const emptyBookingRules = (): BookingRules => ({
  allowMultipleBookingsPerParticipant: false,
  maxSessionsPerParticipant: undefined,
  noShowCancellationLimit: undefined,
  allowConcurrentModeratorBookings: false,
  maxConcurrentModeratorSessions: undefined,
  capGroupBookingSize: false,
  maxGroupSize: undefined,
  capParticipantSessionsInWindow: false,
  windowLimitBasis: undefined,
  windowDays: undefined,
  windowCapacity: undefined,
  maxHoursPerDay: undefined,
});

interface RawBookingRules {
  allow_multiple_bookings_per_participant?: unknown;
  max_sessions_per_participant?: unknown;
  no_show_cancellation_limit?: unknown;
  allow_concurrent_moderator_bookings?: unknown;
  max_concurrent_moderator_sessions?: unknown;
  cap_group_booking_size?: unknown;
  max_group_size?: unknown;
  cap_participant_sessions_in_window?: unknown;
  window_limit_basis?: unknown;
  window_days?: unknown;
  window_capacity?: unknown;
  max_hours_per_day?: unknown;
}

const toOptionalPositiveNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

const toWindowLimitBasis = (value: unknown): 'hours' | 'sessions' | undefined => {
  const basis = String(value || '').toLowerCase();
  return basis === 'hours' || basis === 'sessions' ? basis : undefined;
};

const toOptionalPositiveInt = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : undefined;
};

/**
 * Read Booking Rules off a session type record (the API returns the raw
 * snake_case columns straight through, untyped). Defaults to all-off/unset —
 * the legacy shape — when the session type is null/missing the columns.
 */
export const bookingRulesFromSessionType = (sessionType: unknown): BookingRules => {
  const raw = (sessionType as RawBookingRules | null | undefined) ?? {};
  return {
    allowMultipleBookingsPerParticipant: raw.allow_multiple_bookings_per_participant === true,
    maxSessionsPerParticipant: toOptionalPositiveInt(raw.max_sessions_per_participant),
    noShowCancellationLimit: toOptionalPositiveInt(raw.no_show_cancellation_limit),
    allowConcurrentModeratorBookings: raw.allow_concurrent_moderator_bookings === true,
    maxConcurrentModeratorSessions: toOptionalPositiveInt(raw.max_concurrent_moderator_sessions),
    capGroupBookingSize: raw.cap_group_booking_size === true,
    maxGroupSize: toOptionalPositiveInt(raw.max_group_size),
    capParticipantSessionsInWindow: raw.cap_participant_sessions_in_window === true,
    windowLimitBasis: toWindowLimitBasis(raw.window_limit_basis),
    windowDays: toOptionalPositiveInt(raw.window_days),
    windowCapacity: toOptionalPositiveNumber(raw.window_capacity),
    maxHoursPerDay: toOptionalPositiveNumber(raw.max_hours_per_day),
  };
};

/**
 * The maximum participants allowed in one group booking for this session type,
 * or `null` when uncapped (the default, including every session type configured
 * before the cap existed).
 *
 * Single source of truth for the cap — every group booking entry point should
 * ask this rather than reading the columns directly, so they can't drift.
 */
export const resolveGroupBookingMax = (sessionType: unknown): number | null => {
  const rules = bookingRulesFromSessionType(sessionType);
  if (!rules.capGroupBookingSize) return null;
  return rules.maxGroupSize ?? null;
};
